'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '@terra/ui-design-system';
import type { ReportsReturnDto } from '@terra/shared-contracts-reports';

export function useReportDelete({
  deleteReport,
  onDeleted,
}: {
  deleteReport: (reportId: string) => Promise<void>;
  onDeleted: (report: ReportsReturnDto) => void;
}) {
  const t = useTranslations('proxy.reports.delete');
  const [reportToDelete, setReportToDelete] = useState<ReportsReturnDto | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const request = useCallback((report: ReportsReturnDto) => {
    setReportToDelete(report);
  }, []);
  const cancel = useCallback(() => {
    setReportToDelete(null);
  }, []);
  const confirm = useCallback(async () => {
    if (!reportToDelete) {
      return;
    }

    setIsDeleting(true);

    try {
      await deleteReport(reportToDelete.id);
      toasterApi.success({
        title: t('successTitle'),
        description: t('successDescription'),
      });
      onDeleted(reportToDelete);
      setReportToDelete(null);
    } catch (error) {
      console.error('Failed to delete report:', error);
      toasterApi.error({
        title: t('errorTitle'),
        description: t('errorDescription'),
      });
    } finally {
      setIsDeleting(false);
    }
  }, [deleteReport, onDeleted, reportToDelete, t]);

  return {
    reportToDelete,
    isDialogOpen: reportToDelete !== null,
    isDeleting,
    request,
    cancel,
    confirm,
  };
}
